import { Button } from '@HoudiniUI/Button';
import { Checkbox } from '@HoudiniUI/Checkbox';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '@HoudiniUI/Collapsible';
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '@HoudiniUI/Dialog';
import { Label } from '@HoudiniUI/Label';
import {
	invalidateTelephonyGetConfiguration,
	useTelephonyCampaignResubmitMutation,
	useTelephonyConfigBrandOTPMutation,
	useTelephonyConfigurationForceAllowClientProfileEditMutation,
} from '@Queries/index';
import * as Api from '@ViewModels';
import type { StyleDeclarationValue } from 'aphrodite';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { InternalOnly } from '../../../../components/InternalOnly';
import {
	DerivedCompliance,
	StatusTone,
	filterCampaignsForDisplay,
	formatDateTime,
	getBrandStatusTone,
	getCampaignStatusTone,
} from './complianceDerived';
import { styleSheet } from './styles';

const REGISTRATION_FORM_URL = 'https://secure.levitate.ai/#/texting/registration/form';

const dotStyles: Record<StatusTone, StyleDeclarationValue> = {
	green: styleSheet.dotGreen,
	amber: styleSheet.dotAmber,
	red: styleSheet.dotRed,
	gray: styleSheet.dotGray,
};

const stateStyles: Record<StatusTone, StyleDeclarationValue> = {
	green: styleSheet.statusStateGreen,
	amber: styleSheet.statusStateAmber,
	red: styleSheet.statusStateRed,
	gray: styleSheet.statusStateGray,
};

const StatusLine = ({ who, status, tone, note }: { who: string; status: string; tone: StatusTone; note?: string }) => (
	<span className={css(styleSheet.statusItem)}>
		<span className={css(styleSheet.statusWho)}>{who}</span>
		<span className={css(styleSheet.statusState, stateStyles[tone])}>
			<span className={css(styleSheet.dot, dotStyles[tone])} />
			{status}
		</span>
		{note ? <span className={css(styleSheet.statusNote)}>{note}</span> : null}
	</span>
);

const StatusCard = ({
	who,
	status,
	tone,
	rows,
	errorMessage,
	note,
	children,
}: React.PropsWithChildren<{
	who: string;
	status: string;
	tone: StatusTone;
	rows?: Array<{ label: string; value: string }>;
	errorMessage?: string;
	note?: string;
}>) => (
	<div className={css(styleSheet.statusCard)}>
		<span className={css(styleSheet.statusWho)}>{who}</span>
		<div className={css(styleSheet.statusCardState)}>
			<span className={css(styleSheet.dot, dotStyles[tone])} />
			{status}
		</div>
		{rows?.length ? (
			<dl className={css(styleSheet.statusCardRows)}>
				{rows.map(row => (
					<React.Fragment key={row.label}>
						<dt className={css(styleSheet.statusCardRowLabel)}>{row.label}</dt>
						<dd className={css(styleSheet.statusCardRowValue)}>{row.value}</dd>
					</React.Fragment>
				))}
			</dl>
		) : null}
		{note ? <p className={css(styleSheet.statusCardNote)}>{note}</p> : null}
		{errorMessage ? <p className={css(styleSheet.statusCardError)}>{errorMessage}</p> : null}
		{children}
	</div>
);

const predictedCodesForDecline = (compliance: DerivedCompliance, campaignId?: string) => {
	if (!campaignId) {
		return [];
	}
	const correlation = compliance.review?.declineCorrelations?.find(c => c.campaignId === campaignId);
	return (correlation?.codes ?? []).filter(code => code.wasPredicted).flatMap(code => code.levitateCodes ?? []);
};

export const RegistrationStatusPanel = ({
	config,
	compliance,
	impersonationContext,
	defaultOpen,
	holdActive,
}: {
	config: Api.TelephonyServiceProviderConfiguration;
	compliance: DerivedCompliance;
	impersonationContext: Api.IImpersonationContext;
	defaultOpen: boolean;
	holdActive: boolean;
}) => {
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('TextingRegistrationStatusPanel');
	const [open, setOpen] = React.useState(defaultOpen);
	const [campaignToResubmit, setCampaignToResubmit] = React.useState<Api.TextingProviderCampaign | null>(null);
	const [resubmitConfirmed, setResubmitConfirmed] = React.useState(false);
	const [showAllowEditConfirmation, setShowAllowEditConfirmation] = React.useState(false);
	const [showResendOTPConfirmation, setShowResendOTPConfirmation] = React.useState(false);

	// Manual toggling wins between changes, but an in-page change to the rule (e.g. a passing re-check)
	// must take effect.
	React.useEffect(() => {
		setOpen(defaultOpen);
	}, [defaultOpen]);

	const brand = config.campaignRegistration?.brand;
	const campaigns = filterCampaignsForDisplay(config.campaignRegistration?.campaigns ?? []);
	const businessType = config.clientProfile?.businessInformation?.type;
	const hasSupportLevel3 = userSession.hasPermission(AdminPermission.SupportLevel3);

	const closeResubmitDialog = () => {
		setCampaignToResubmit(null);
		setResubmitConfirmed(false);
	};

	const resubmitMutation = useTelephonyCampaignResubmitMutation({
		onSuccess: () => {
			invalidateTelephonyGetConfiguration();
			toast.success('Campaign resubmitted.');
			closeResubmitDialog();
		},
		onError: error => {
			logApiError('ResubmitTelephonyCampaign', error);
			errorMessages.pushApiError(error);
		},
	});
	const allowEditMutation = useTelephonyConfigurationForceAllowClientProfileEditMutation({
		onSuccess: () => {
			invalidateTelephonyGetConfiguration();
			toast.success('Registration edits allowed.');
		},
		onError: error => {
			logApiError('TelephonyForceAllowClientProfileEdit', error);
			errorMessages.pushApiError(error);
		},
	});
	const brandOTPMutation = useTelephonyConfigBrandOTPMutation({
		onSuccess: () => {
			toast.success('OTP resent successfully');
		},
		onError: error => {
			logApiError('TelephonyBrandResendOTP', error);
			errorMessages.pushApiError(error);
		},
	});

	// Looser than the old strip on purpose: a client live on one provider may still need profile changes
	// before submitting to another.
	const canAllowEdits = hasSupportLevel3 && !config.forceAllowClientProfileEdit;
	const canRetryOTP =
		hasSupportLevel3 && brand?.status === Api.TextingBrandStatus.Pending && businessType === 'SoleProprietorship';

	const brandNote =
		brand?.status === Api.TextingBrandStatus.Pending && businessType === 'SoleProprietorship'
			? 'On hold pending sole proprietorship support. If the customer is not a sole proprietor, please reset.'
			: undefined;

	return (
		<Collapsible open={open} onOpenChange={setOpen} className={css(styleSheet.statusBox)}>
			<CollapsibleTrigger className={css(styleSheet.statusBar)}>
				{brand ? (
					<StatusLine who='Brand' status={brand.status} tone={getBrandStatusTone(brand.status)} note={businessType} />
				) : null}
				{campaigns.map((campaign, index) => (
					<StatusLine
						key={`${campaign.id ?? campaign.provider}-${index}`}
						who={campaign.provider}
						status={campaign.status}
						tone={getCampaignStatusTone(campaign.status)}
					/>
				))}
				{!brand && !campaigns.length ? <StatusLine who='Registration' status='Not submitted' tone='gray' /> : null}
				<span className={css(styleSheet.statusToggle)}>
					Registration details
					<span aria-hidden>{open ? '▴' : '▾'}</span>
				</span>
			</CollapsibleTrigger>
			<CollapsiblePanel>
				<div className={css(styleSheet.statusPanel)}>
					<div className={css(styleSheet.statusGrid)}>
						{brand ? (
							<StatusCard
								who='Brand'
								status={brand.status}
								tone={getBrandStatusTone(brand.status)}
								rows={[
									...(brand.id ? [{ label: 'Id', value: brand.id }] : []),
									...(businessType ? [{ label: 'Type', value: businessType }] : []),
								]}
								note={brandNote}
								errorMessage={brand.errorMessage}
							/>
						) : null}
						{campaigns.map((campaign, index) => {
							const predictedCodes = predictedCodesForDecline(compliance, campaign.id);
							const canResubmit =
								hasSupportLevel3 &&
								(campaign.status === Api.CampaignRegistrationStatus.Failed ||
									campaign.status === Api.CampaignRegistrationStatus.Declined);
							return (
								<StatusCard
									key={`${campaign.id ?? campaign.provider}-${index}`}
									who={campaign.provider}
									status={campaign.status}
									tone={getCampaignStatusTone(campaign.status)}
									rows={[
										...(campaign.id ? [{ label: 'Id', value: campaign.id }] : []),
										...(campaign.lastSubmissionDate
											? [{ label: 'Submitted', value: formatDateTime(campaign.lastSubmissionDate) }]
											: []),
									]}
									errorMessage={campaign.errorMessage}
								>
									{predictedCodes.length ? (
										<InternalOnly>
											<p className={css(styleSheet.statusCardNote)}>
												↳ this decline matched a predicted finding, {predictedCodes.join(', ')}.
											</p>
										</InternalOnly>
									) : null}
									{canResubmit ? (
										<div className={css(styleSheet.actions)}>
											<Button variant='outline' size='sm' onClick={() => setCampaignToResubmit(campaign)}>
												Resubmit campaign…
											</Button>
										</div>
									) : null}
								</StatusCard>
							);
						})}
						{!campaigns.length ? (
							<StatusCard
								who='Campaigns'
								status='Not submitted'
								tone='gray'
								note={
									holdActive
										? 'The hold stopped a campaign being created, so there is nothing at the provider yet.'
										: undefined
								}
							/>
						) : null}
					</div>
					{canAllowEdits || canRetryOTP ? (
						<div className={css(styleSheet.panelActions)}>
							{canAllowEdits ? (
								<Button variant='outline' size='sm' onClick={() => setShowAllowEditConfirmation(true)}>
									Allow registration edits…
								</Button>
							) : null}
							{canRetryOTP ? (
								<Button variant='outline' size='sm' onClick={() => setShowResendOTPConfirmation(true)}>
									Resend brand OTP…
								</Button>
							) : null}
						</div>
					) : null}
				</div>
			</CollapsiblePanel>
			<Dialog open={!!campaignToResubmit} onOpenChange={isOpen => (!isOpen ? closeResubmitDialog() : undefined)}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Resubmit campaign</DialogTitle>
					</DialogHeader>
					<DialogBody styles={[styleSheet.dialogStack]}>
						<p className={css(styleSheet.dialogText)}>
							Please ensure the customer has addressed the error reason before resubmitting the campaign. If you believe
							this is in error, we can submit an appeal.
						</p>
						<Label styles={[styleSheet.checkboxLabel]}>
							<Checkbox
								checked={resubmitConfirmed}
								onCheckedChange={checked => setResubmitConfirmed(checked === true)}
							/>
							I confirm issues have been addressed
						</Label>
					</DialogBody>
					<DialogFooter>
						<Button variant='outline' onClick={closeResubmitDialog}>
							Cancel
						</Button>
						<Button
							disabled={!resubmitConfirmed}
							isLoading={resubmitMutation.isPending}
							onClick={() => resubmitMutation.mutate({ impersonationContext, campaignId: campaignToResubmit.id })}
						>
							Resubmit campaign
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
			<Dialog open={showAllowEditConfirmation} onOpenChange={setShowAllowEditConfirmation}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Allow editing existing texting registration</DialogTitle>
					</DialogHeader>
					<DialogBody styles={[styleSheet.dialogStack]}>
						<p className={css(styleSheet.dialogText)}>
							Allow customers to make texting campaign changes. Once complete, customers can go directly to the
							registration form:
						</p>
						<p className={css(styleSheet.dialogText)}>
							<a
								className={css(styleSheet.registrationFormLink)}
								href={REGISTRATION_FORM_URL}
								target='_blank'
								rel='noreferrer'
							>
								{REGISTRATION_FORM_URL}
							</a>
						</p>
					</DialogBody>
					<DialogFooter>
						<Button variant='outline' onClick={() => setShowAllowEditConfirmation(false)}>
							Cancel
						</Button>
						<Button
							isLoading={allowEditMutation.isPending}
							onClick={() => {
								allowEditMutation.mutate({ impersonationContext });
								setShowAllowEditConfirmation(false);
							}}
						>
							Allow registration edits
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
			<Dialog open={showResendOTPConfirmation} onOpenChange={setShowResendOTPConfirmation}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Resend brand verification text?</DialogTitle>
					</DialogHeader>
					<DialogBody styles={[styleSheet.dialogStack]}>
						<p className={css(styleSheet.dialogText)}>
							Are you sure you want to resend a brand verification text to the user?
						</p>
					</DialogBody>
					<DialogFooter>
						<Button variant='outline' onClick={() => setShowResendOTPConfirmation(false)}>
							Cancel
						</Button>
						<Button
							isLoading={brandOTPMutation.isPending}
							onClick={() => {
								brandOTPMutation.mutate({ impersonationContext });
								setShowResendOTPConfirmation(false);
							}}
						>
							Resend OTP
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</Collapsible>
	);
};
